import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled, { keyframes, css } from 'styled-components';
// Cut quality grades with star ratings
const CUT_CONFIG = {
    'EX': { stars: 5, label: 'Excellent', color: '#996515' },
    'ID': { stars: 5, label: 'Ideal', color: '#996515' },
    'VG': { stars: 4, label: 'Very Good', color: '#b8860b' },
    'G': { stars: 3, label: 'Good', color: '#d4a853' },
    'F': { stars: 2, label: 'Fair', color: '#a3a3a3' },
    'P': { stars: 1, label: 'Poor', color: '#737373' },
    'I': { stars: 2, label: 'Ideal', color: '#996515' },
};
const shimmer = keyframes `
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
`;
const shimmerAnimation = css `
  animation: ${shimmer} 2s ease-in-out infinite;
`;
const CutQualityContainer = styled.div `
  margin-bottom: 20px;
`;
const FilterSectionTitle = styled.h4 `
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  margin-top: 0;
`;
const CutQualityGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(65px, 1fr));
  gap: 6px;
`;
const StarRating = styled.div `
  display: flex;
  gap: 1px;
  justify-content: center;
  margin-bottom: 4px;
  height: 12px;
  align-items: center;

  .star {
    font-size: 10px;
    color: var(--text-tertiary);
    transition: all 0.2s ease;
  }

  .star.filled {
    color: ${props => props.$starColor};
    ${props => props.$isExcellent && shimmerAnimation}
  }
`;
const CutPill = styled.button `
  padding: 8px 12px;
  min-width: 65px;
  background: ${props => props.$disabled ? 'var(--bg-tertiary)' :
    props.$isActive ? 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.$disabled ? 'var(--border-color)' :
    props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--border-color)'};
  color: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--text-secondary)'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  opacity: ${props => props.$disabled ? 0.4 : 1};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  &:hover {
    border-color: ${props => props.$disabled ? 'var(--border-color)' : 'var(--accent-gold-border, rgba(153, 101, 21, 0.25))'};
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 8px rgba(0,0,0,0.1)'};
  }

  &:hover ${StarRating} .star.filled {
    transform: scale(1.2);
  }

  .cut-label {
    font-weight: 600;
    font-size: 12px;
  }

  .cut-count {
    font-size: 10px;
    color: var(--text-tertiary);
  }
`;
const AllCutsPill = styled(CutPill) `
  background: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--bg-secondary)'};
  color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  font-weight: 600;
  min-width: 65px;

  .cut-label {
    color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  }

  .cut-count {
    color: ${props => props.$isActive ? 'rgba(255,255,255,0.8)' : 'var(--text-tertiary)'};
  }
`;
export const CutQualityFilter = ({ selectedCut, cuts, onCutChange, getCutCount, cutQuality }) => {
    const totalCount = cuts.reduce((sum, cut) => sum + getCutCount(cut), 0);
    const renderStars = (cut) => {
        const config = CUT_CONFIG[cut];
        if (!config)
            return null;
        const maxStars = 5;
        const filledStars = config.stars;
        const isExcellent = cut === 'EX' || cut === 'ID';
        return (_jsx(StarRating, { "$starColor": config.color, "$isExcellent": isExcellent, children: [...Array(maxStars)].map((_, i) => (_jsx("span", { className: `star ${i < filledStars ? 'filled' : ''}`, children: i < filledStars ? '★' : '☆' }, i))) }));
    };
    return (_jsxs(CutQualityContainer, { children: [_jsx(FilterSectionTitle, { children: "Cut Quality" }), _jsxs(CutQualityGrid, { children: [_jsxs(AllCutsPill, { "$isActive": !selectedCut, onClick: () => onCutChange(''), title: `All Cuts (${totalCount} diamonds)`, children: [_jsx("span", { className: "cut-label", children: "All" }), _jsx("span", { className: "cut-count", children: totalCount })] }), cuts.map(cut => {
                        const count = getCutCount(cut);
                        const isDisabled = count === 0;
                        const config = CUT_CONFIG[cut];
                        return (_jsxs(CutPill, { "$isActive": selectedCut === cut, "$disabled": isDisabled, "$cutColor": config?.color, onClick: () => !isDisabled && onCutChange(cut), title: `${config?.label || cut} (${count} diamonds)`, children: [renderStars(cut), _jsx("span", { className: "cut-label", children: cut }), _jsx("span", { className: "cut-count", children: count })] }, cut));
                    })] })] }));
};
